import {
  createEffect,
  createMemo,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { ALPHA_MIN, simulation } from "../domaine/force";
import {
  aretes as aretesAuSeuil,
  aretesDePlacement,
  classesColorees,
  emplacementCouleur,
  type Placement,
} from "../domaine/graphe";
import type { Similarites } from "../domaine/similarite";
import type { Portefeuille } from "../domaine/types";
import { formaterScore, pluriel } from "../format";
import { declarerFigure } from "./Figure";

// Vue phare du POC (poc/src/graphe.js) : un disque par projet, un trait entre deux
// projets dont la ressemblance dépasse le seuil. Les grappes sont les groupes, les
// disques détachés les projets sans équivalent.
//
// Encodage (ADR-0003 du POC) : la couleur est réservée à la classe de bioagresseur,
// trois teintes au plus ; un projet à plusieurs cibles a un disque en secteurs, pas
// une teinte de plus. La mise en avant atténue le reste plutôt que d'ajouter une couleur.

const RAYON = 13;
const MARGE = 16;
// Largeur moyenne d'un caractère de l'acronyme à sa taille de rendu (mesurée dans le POC).
const LARGEUR_CARACTERE = 6.4;
const LARGEUR_DEFAUT = 800;
const HAUTEUR_DEFAUT = 560;
// Le dessin suit les positions calculées avec un retard du premier ordre : 0,22
// par image, soit environ 75 ms. Assez pour ôter la sécheresse, trop peu pour
// une impression de latence (mesures dans poc/src/graphe.js).
const LISSAGE = 0.22;
// Température tenue pendant un glissement : 0,22 double le suivi du voisinage par
// rapport à 0,15 sans faire osciller (mesures dans poc/src/graphe.js).
const TEMPERATURE_GLISSEMENT = 0.22;
// En deçà, un clic qui tremble n'est pas un glissement.
const SEUIL_GLISSEMENT = 3;
// Un filtre ou un seuil qui change redispose le graphe : chaque projet glisse de
// sa place à la nouvelle plutôt que de sauter, pour qu'on le suive des yeux.
const DUREE_TRANSITION = 480;
const adoucir = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const mouvementReduit = () =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
const maintenant = () => (typeof performance === "undefined" ? Date.now() : performance.now());

interface Vue {
  k: number;
  tx: number;
  ty: number;
}

interface Point {
  x: number;
  y: number;
}

/** Secteur de disque centré sur l'origine, pour les projets à plusieurs cibles. */
export function secteur(rayon: number, debut: number, fin: number): string {
  const x0 = rayon * Math.cos(debut);
  const y0 = rayon * Math.sin(debut);
  const x1 = rayon * Math.cos(fin);
  const y1 = rayon * Math.sin(fin);
  const grand = fin - debut > Math.PI ? 1 : 0;
  return `M 0 0 L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${rayon} ${rayon} 0 ${grand} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
}

const couleur = (emplacement: number | null) =>
  emplacement === null ? "var(--cible-autre)" : `var(--cible-${emplacement})`;

const image = (f: () => void): number =>
  typeof requestAnimationFrame === "function"
    ? requestAnimationFrame(f)
    : (setTimeout(f, 16) as unknown as number);
const annulerImage = (id: number) =>
  typeof cancelAnimationFrame === "function" ? cancelAnimationFrame(id) : clearTimeout(id);

export function GrapheSimilarite(props: {
  portefeuille: Portefeuille;
  similarites: Similarites;
  /** Référence du projet sélectionné, partagée avec le panneau. */
  selection: string | null;
  /** Indices des projets trouvés par la recherche ; null sans recherche. */
  misEnAvant: ReadonlySet<number> | null;
  surSelection: (ref: string | null) => void;
  seuil: number;
  /** Ce qui tient le placement : plus proches voisins (défaut) ou liens tracés. */
  placement?: Placement;
  /** Donne à l'appelant de quoi recadrer, depuis la barre de commandes. */
  controle?: (c: { recadrer: () => void }) => void;
}) {
  const projets = () => props.portefeuille.projets;
  const [cadre, setCadre] = createSignal({ largeur: LARGEUR_DEFAUT, hauteur: HAUTEUR_DEFAUT });
  const [survol, setSurvol] = createSignal<number | null>(null);

  const liens = createMemo(() => aretesAuSeuil(props.similarites, props.seuil));
  const colorees = createMemo(() => classesColorees(props.portefeuille.classesBioagresseur));
  const voisinage = createMemo(() => {
    const v = projets().map(() => new Set<number>());
    for (const e of liens()) {
      v[e.a]?.add(e.b);
      v[e.b]?.add(e.a);
    }
    return v;
  });

  // Placement tenu par les plus proches voisins de chaque projet, tracés ou non
  // (défaut, ADR-0018 : il ne dépend pas du seuil), ou par les seuls liens tracés.
  // La simulation est déterministe : les mêmes ressorts redonnent le même dessin.
  const ressorts = createMemo(() =>
    props.placement === "liens" ? liens() : aretesDePlacement(props.similarites),
  );
  const disposition = createMemo(() => {
    const { largeur, hauteur } = cadre();
    // Encombrement mesuré sur la donnée : l'acronyme le plus long fixe la largeur ;
    // la hauteur prend le rapport de forme du cadre pour que le nuage le remplisse.
    const caracteres = projets().reduce((m, p) => Math.max(m, p.acronyme.length), 1);
    const demiLargeur = Math.max(RAYON + 6, (caracteres * LARGEUR_CARACTERE) / 2 + 7);
    const demiHauteur = Math.max(RAYON + 15, (demiLargeur * hauteur) / largeur);
    const sim = simulation(
      projets().length,
      ressorts().map((e) => ({ source: e.a, cible: e.b, poids: e.score })),
      { largeur, hauteur, rayonX: demiLargeur, rayonY: demiHauteur },
    );
    sim.stabiliser();
    // La boîte est prise au repos : un glissement ne doit pas faire bouger la
    // vue sous la main.
    return { sim, boite: sim.boite(), demiLargeur, largeur, hauteur };
  });

  // Zoom et déplacement de l'utilisateur ; null = cadrage automatique sur le graphe.
  const [vueManuelle, setVueManuelle] = createSignal<Vue | null>(null);

  // Positions dessinées, en retard sur les calculées pendant un glissement.
  const [positions, setPositions] = createSignal<Point[]>([]);
  let affiche: Point[] = [];
  /** Dernière place dessinée de chaque projet, par référence : les indices changent avec les filtres. */
  const places = new Map<string, Point>();
  const afficher = (points: Point[]) => {
    affiche = points;
    projets().forEach((p, j) => {
      const pt = points[j];
      if (pt) places.set(p.ref, pt);
    });
    setPositions(points);
  };
  // Cadrage pendant une transition ; null le reste du temps.
  const [vueTransition, setVueTransition] = createSignal<Vue | null>(null);
  const [enTransition, setEnTransition] = createSignal(false);
  /** Projets apparus à la dernière transition, qui entrent en fondu. */
  const [entrants, setEntrants] = createSignal<ReadonlySet<string>>(new Set());
  let animation: number | null = null;
  let glissant: number | null = null;

  const arreterAnimation = () => {
    if (animation !== null) annulerImage(animation);
    animation = null;
  };
  onCleanup(arreterAnimation);

  createEffect(
    on(disposition, (d, ancienne) => {
      arreterAnimation();
      const cibles = d.sim.noeuds().map((n) => ({ x: n.x, y: n.y }));
      // Le cadrage de départ est celui qu'on voyait : zoom de l'utilisateur ou
      // cadrage automatique de l'ancienne disposition.
      /* eslint-disable solid/reactivity -- instantanés voulus : le départ de la transition */
      const vueDepart =
        vueTransition() ?? vueManuelle() ?? (ancienne ? cadragePour(ancienne) : null);
      setVueManuelle(null);
      // Seul un changement de la donnée (filtre, seuil, pondération) s'anime. Un
      // cadre qui change — la première mesure en revenant sur la vue, une fenêtre
      // redimensionnée — se pose sans transition.
      const memeCadre =
        !!ancienne && ancienne.largeur === d.largeur && ancienne.hauteur === d.hauteur;
      if (!ancienne || !memeCadre || !vueDepart || mouvementReduit()) {
        setVueTransition(null);
        afficher(cibles);
        return;
      }
      // Un projet déjà dessiné part de sa place ; un nouveau venu naît à la sienne
      // (et apparaît en fondu, voir la feuille de style).
      const departs = projets().map((p, j) => places.get(p.ref) ?? cibles[j] ?? { x: 0, y: 0 });
      /* eslint-enable solid/reactivity */
      const vueArrivee = cadragePour(d);
      // Tout de suite, sans attendre la première image : les indices ont changé,
      // et les anciennes positions mises aux nouveaux indices feraient sauter les nœuds.
      afficher(departs);
      setVueTransition(vueDepart);
      // Le fondu des nouveaux venus ne vaut que pendant la transition : pas à
      // chaque retour sur la vue.
      setEntrants(new Set(projets().flatMap((p) => (places.has(p.ref) ? [] : [p.ref]))));
      setEnTransition(true);
      const debut = maintenant();
      const pas = () => {
        const t = Math.min(1, (maintenant() - debut) / DUREE_TRANSITION);
        const e = adoucir(t);
        afficher(
          departs.map((a, j) => {
            const c = cibles[j] ?? a;
            return { x: a.x + (c.x - a.x) * e, y: a.y + (c.y - a.y) * e };
          }),
        );
        setVueTransition(
          t < 1
            ? {
                k: vueDepart.k + (vueArrivee.k - vueDepart.k) * e,
                tx: vueDepart.tx + (vueArrivee.tx - vueDepart.tx) * e,
                ty: vueDepart.ty + (vueArrivee.ty - vueDepart.ty) * e,
              }
            : null,
        );
        animation = t < 1 ? image(pas) : null;
        // Les liens finissent leur fondu un peu après les nœuds.
        if (t >= 1) setTimeout(() => setEnTransition(false), 160);
      };
      animation = image(pas);
    }),
  );

  /** Reporte les positions calculées sur le dessin ; rend l'écart restant. */
  const positionner = (): number => {
    const noeuds = disposition().sim.noeuds();
    let reste = 0;
    affiche = affiche.map((a, j) => {
      const n = noeuds[j];
      if (!n) return a;
      // Le nœud tenu colle au curseur : un retard sur lui se sentirait comme une latence.
      const k = glissant === j ? 1 : LISSAGE;
      const x = a.x + (n.x - a.x) * k;
      const y = a.y + (n.y - a.y) * k;
      reste = Math.max(reste, Math.abs(n.x - x), Math.abs(n.y - y));
      return { x, y };
    });
    afficher(affiche);
    return reste;
  };

  const animer = () => {
    if (animation !== null) return;
    const pas = () => {
      const alpha = disposition().sim.pas();
      // On continue tant que la physique bouge ou que le dessin la rattrape.
      const reste = positionner();
      animation = alpha > ALPHA_MIN || reste > 0.4 ? image(pas) : null;
    };
    animation = image(pas);
  };

  const cadrage = (): Vue => cadragePour(disposition());
  function cadragePour(d: {
    boite: { x0: number; y0: number; largeur: number; hauteur: number };
    demiLargeur: number;
  }): Vue {
    const { largeur, hauteur } = cadre();
    // Le cadrage compte l'encombrement des acronymes, pas seulement les centres :
    // sinon l'étiquette d'un nœud placé au bord sort du cadre.
    const { boite, demiLargeur } = d;
    const b = {
      x0: boite.x0 - demiLargeur,
      y0: boite.y0 - RAYON,
      largeur: boite.largeur + 2 * demiLargeur,
      hauteur: boite.hauteur + 2 * RAYON + 14,
    };
    const k = Math.max(
      0.35,
      Math.min(
        1.8,
        (largeur - 2 * MARGE) / Math.max(b.largeur, 1),
        (hauteur - 2 * MARGE) / Math.max(b.hauteur, 1),
      ),
    );
    return {
      k,
      tx: largeur / 2 - k * (b.x0 + b.largeur / 2),
      ty: hauteur / 2 - k * (b.y0 + b.hauteur / 2),
    };
  }
  const vue = () => vueTransition() ?? vueManuelle() ?? cadrage();
  // eslint-disable-next-line solid/reactivity -- donné une fois au montage, comme une ref
  props.controle?.({ recadrer: () => setVueManuelle(null) });

  // Épaisseur bornée aux scores réellement présents : au seuil haut, toutes les
  // arêtes seraient au maximum sur une échelle [0, 1].
  const traits = createMemo(() => {
    const scores = liens().map((e) => e.score);
    const sMin = scores.length ? Math.min(...scores) : 0;
    const sMax = scores.length ? Math.max(...scores) : 1;
    return liens().map((e) => {
      const t = sMax > sMin ? (e.score - sMin) / (sMax - sMin) : 0.5;
      return { ...e, epaisseur: 1.2 + 2.6 * t, opacite: 0.38 + 0.44 * t };
    });
  });

  const indiceSelection = () => {
    const i = projets().findIndex((p) => p.ref === props.selection);
    return i === -1 ? null : i;
  };
  const actif = () => survol() ?? indiceSelection();
  const lie = (i: number) => {
    const a = actif();
    return a !== null && (i === a || (voisinage()[a]?.has(i) ?? false));
  };

  const choisir = (i: number) => {
    const ref = projets()[i]?.ref ?? null;
    props.surSelection(ref === props.selection ? null : ref);
  };

  // --- zoom et déplacement du fond -------------------------------------------

  let conteneur: HTMLDivElement | undefined;
  let svg: SVGSVGElement | undefined;
  let deplacement: { x: number; y: number; depart: Vue } | null = null;
  const [enDeplacement, setEnDeplacement] = createSignal(false);

  onMount(() => {
    if (!conteneur || !svg) return;
    const mesurer = () => {
      const boite = conteneur?.getBoundingClientRect();
      if (!boite || boite.width === 0) return;
      const largeur = Math.max(320, Math.round(boite.width));
      const hauteur = Math.max(280, Math.round(boite.height));
      const c = cadre();
      if (c.largeur !== largeur || c.hauteur !== hauteur) setCadre({ largeur, hauteur });
    };
    mesurer();
    if (typeof ResizeObserver !== "undefined") {
      let minuteur: ReturnType<typeof setTimeout> | undefined;
      const observateur = new ResizeObserver(() => {
        clearTimeout(minuteur);
        minuteur = setTimeout(mesurer, 150);
      });
      observateur.observe(conteneur);
      onCleanup(() => {
        clearTimeout(minuteur);
        observateur.disconnect();
      });
    }

    // Ajouté à la main : `onWheel` de Solid est passif, on ne pourrait pas empêcher
    // la page de défiler pendant le zoom.
    const zoomer = (e: WheelEvent) => {
      e.preventDefault();
      const boite = svg?.getBoundingClientRect();
      if (!boite) return;
      const v = vue();
      const px = ((e.clientX - boite.left) / boite.width) * cadre().largeur;
      const py = ((e.clientY - boite.top) / boite.height) * cadre().hauteur;
      const k = Math.max(0.3, Math.min(6, v.k * Math.exp(-e.deltaY * 0.0016)));
      // Zoom centré sur le pointeur : le point sous la souris ne bouge pas.
      setVueManuelle({ k, tx: px - (px - v.tx) * (k / v.k), ty: py - (py - v.ty) * (k / v.k) });
      setSurvol(null);
    };
    svg.addEventListener("wheel", zoomer, { passive: false });
    onCleanup(() => svg?.removeEventListener("wheel", zoomer));
  });

  /** Unités du dessin par pixel d'écran. */
  const echelleEcran = () => {
    const boite = svg?.getBoundingClientRect();
    return boite && boite.width > 0 ? cadre().largeur / boite.width : 1;
  };

  const debuterDeplacement = (e: PointerEvent) => {
    deplacement = { x: e.clientX, y: e.clientY, depart: vue() };
    try {
      svg?.setPointerCapture(e.pointerId);
    } catch {
      // La capture est un confort : elle échoue sur un pointeur déjà relâché.
    }
    setEnDeplacement(true);
  };
  const deplacer = (e: PointerEvent) => {
    if (!deplacement) return;
    const f = echelleEcran();
    setVueManuelle({
      k: deplacement.depart.k,
      tx: deplacement.depart.tx + (e.clientX - deplacement.x) * f,
      ty: deplacement.depart.ty + (e.clientY - deplacement.y) * f,
    });
  };
  const finirDeplacement = () => {
    deplacement = null;
    setEnDeplacement(false);
  };

  // --- glissement d'un nœud ----------------------------------------------------
  //
  // Le nœud suit le pointeur et son voisinage suit, la simulation tenue tiède.
  // Relâché, il redevient flottant et reprend sa place dans le jeu des forces : un
  // nœud figé ne se laisserait plus pousser par ses voisins, et rien ne dirait
  // lesquels le sont.

  let glisse: { i: number; x0: number; y0: number; nx: number; ny: number; bouge: boolean } | null =
    null;
  // Le clic qui suit un glissement ne sélectionne pas.
  let vientDeGlisser = false;

  const saisirNoeud = (e: PointerEvent, i: number) => {
    e.stopPropagation();
    const n = disposition().sim.noeuds()[i];
    if (!n) return;
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      // Sans conséquence : la capture est un confort.
    }
    glisse = { i, x0: e.clientX, y0: e.clientY, nx: n.x, ny: n.y, bouge: false };
  };
  const glisserNoeud = (e: PointerEvent) => {
    if (!glisse) return;
    const f = echelleEcran() / vue().k;
    const dx = (e.clientX - glisse.x0) * f;
    const dy = (e.clientY - glisse.y0) * f;
    if (!glisse.bouge && Math.abs(dx) + Math.abs(dy) < SEUIL_GLISSEMENT) return;
    const sim = disposition().sim;
    if (!glisse.bouge) {
      // Une seule fois : régler la température à chaque mouvement l'empêcherait
      // de refroidir. On part de 0,08 pour éviter l'à-coup du premier mouvement.
      sim.cibler(TEMPERATURE_GLISSEMENT);
      sim.relancer(Math.max(sim.alpha(), 0.08));
      glisse.bouge = true;
      setSurvol(null);
    }
    glissant = glisse.i;
    sim.fixer(glisse.i, glisse.nx + dx, glisse.ny + dy);
    // Redessiné tout de suite : le nœud tenu doit coller au curseur même si le
    // navigateur rend moins souvent qu'il ne reçoit d'événements.
    positionner();
    animer();
  };
  const relacherNoeud = (e: PointerEvent) => {
    if (!glisse) return;
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      // Déjà relâché.
    }
    glissant = null;
    const sim = disposition().sim;
    sim.cibler(0);
    if (glisse.bouge) {
      vientDeGlisser = true;
      sim.liberer(glisse.i);
      sim.relancer(Math.max(sim.alpha(), 0.15));
      animer();
    }
    glisse = null;
  };

  // --- infobulle ----------------------------------------------------------------

  const infobulle = createMemo(() => {
    const i = survol();
    if (i === null) return null;
    const projet = projets()[i];
    const position = positions()[i];
    if (!projet || !position) return null;
    const proche = props.similarites.voisins[i]?.[0];
    const v = vue();
    const { largeur, hauteur } = cadre();
    return {
      projet,
      proche: proche ? { acronyme: projets()[proche.autre]?.acronyme, score: proche.score } : null,
      liens: voisinage()[i]?.size ?? 0,
      // En pourcentage du cadre : juste quelle que soit l'échelle d'affichage du SVG.
      gauche: ((v.tx + v.k * position.x) / largeur) * 100,
      haut: ((v.ty + v.k * position.y - RAYON * v.k - 12) / hauteur) * 100,
    };
  });

  const resume = () =>
    `${pluriel(projets().length, "projet")}, ${pluriel(liens().length, "lien")} au seuil ${formaterScore(props.seuil)}.`;

  declarerFigure({
    id: "graphe-similarite",
    svg: () => svg,
    tableau: () => {
      const dims = props.similarites.dimensionsUtilisees;
      const detail = (a: number, b: number) =>
        props.similarites.voisins[a]?.find((v) => v.autre === b)?.detail ?? {};
      return {
        colonnes: ["projet_a", "projet_b", "score", ...dims],
        lignes: liens().map((e) => [
          projets()[e.a]?.acronyme ?? "",
          projets()[e.b]?.acronyme ?? "",
          e.score.toFixed(4),
          ...dims.map((d) => (detail(e.a, e.b)[d] ?? 0).toFixed(4)),
        ]),
      };
    },
  });

  return (
    <div ref={(e) => (conteneur = e)} class="graphe-cadre">
      <svg
        ref={(e) => (svg = e)}
        class="graphe"
        classList={{
          "a-mise-en-avant": actif() !== null || props.misEnAvant !== null,
          "en-deplacement": enDeplacement(),
          "en-transition": enTransition(),
        }}
        viewBox={`0 0 ${cadre().largeur} ${cadre().hauteur}`}
        role="group"
        aria-label={`Graphe de similarité : ${resume()}`}
        onPointerDown={debuterDeplacement}
        onPointerMove={deplacer}
        onPointerUp={finirDeplacement}
        onPointerCancel={finirDeplacement}
        onDblClick={() => setVueManuelle(null)}
      >
        <g
          transform={`translate(${vue().tx.toFixed(2)},${vue().ty.toFixed(2)}) scale(${vue().k.toFixed(4)})`}
        >
          <g class="aretes">
            <For each={traits()}>
              {(e) => {
                const a = () => positions()[e.a];
                const b = () => positions()[e.b];
                return (
                  <line
                    class="arete"
                    classList={{
                      "en-avant": actif() !== null && (e.a === actif() || e.b === actif()),
                    }}
                    x1={a()?.x.toFixed(1)}
                    y1={a()?.y.toFixed(1)}
                    x2={b()?.x.toFixed(1)}
                    y2={b()?.y.toFixed(1)}
                    stroke-width={e.epaisseur.toFixed(2)}
                    stroke-opacity={e.opacite.toFixed(2)}
                  />
                );
              }}
            </For>
          </g>
          <g class="noeuds">
            <For each={projets()}>
              {(projet, i) => {
                const position = () => positions()[i()];
                const cibles = () => (colorees().length ? projet.bioagresseurs : []);
                return (
                  <g
                    class="noeud"
                    classList={{
                      "en-avant": lie(i()) || (props.misEnAvant?.has(i()) ?? false),
                      choisi: props.selection === projet.ref,
                      entrant: enTransition() && entrants().has(projet.ref),
                      source: actif() === i(),
                    }}
                    transform={`translate(${position()?.x.toFixed(1) ?? 0},${position()?.y.toFixed(1) ?? 0})`}
                    tabIndex={0}
                    role="button"
                    aria-pressed={props.selection === projet.ref}
                    aria-label={`${projet.acronyme} — ${pluriel(voisinage()[i()]?.size ?? 0, "lien")} à ce seuil`}
                    onPointerDown={(e) => saisirNoeud(e, i())}
                    onPointerMove={glisserNoeud}
                    onPointerUp={relacherNoeud}
                    onPointerCancel={relacherNoeud}
                    onClick={() => {
                      if (vientDeGlisser) {
                        vientDeGlisser = false;
                        return;
                      }
                      choisir(i());
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        choisir(i());
                      }
                    }}
                    onPointerEnter={() => {
                      if (!glisse) setSurvol(i());
                    }}
                    onPointerLeave={() => {
                      if (!glisse) setSurvol(null);
                    }}
                    onFocus={() => setSurvol(i())}
                    onBlur={() => setSurvol(null)}
                  >
                    <Show
                      when={cibles().length > 1}
                      fallback={
                        <circle
                          class="disque"
                          r={RAYON}
                          fill={couleur(
                            cibles()[0] === undefined
                              ? null
                              : emplacementCouleur(cibles()[0] ?? "", colorees()),
                          )}
                        />
                      }
                    >
                      {/* Plusieurs cibles → secteurs, jamais une teinte de plus. */}
                      <For each={cibles()}>
                        {(c, k) => {
                          const pas = () => (Math.PI * 2) / cibles().length;
                          return (
                            <path
                              class="secteur"
                              d={secteur(
                                RAYON,
                                -Math.PI / 2 + k() * pas(),
                                -Math.PI / 2 + (k() + 1) * pas(),
                              )}
                              fill={couleur(emplacementCouleur(c, colorees()))}
                            />
                          );
                        }}
                      </For>
                      <circle class="disque contour-secteurs" r={RAYON} fill="none" />
                    </Show>
                    <text class="etiquette" y={RAYON + 14} text-anchor="middle">
                      {projet.acronyme}
                    </text>
                    {/* Cible de pointage plus large que la marque. */}
                    <circle class="pointage" r={RAYON + 9} fill="transparent" data-export="non" />
                  </g>
                );
              }}
            </For>
          </g>
        </g>
      </svg>
      <Show when={infobulle()}>
        {(info) => (
          <div
            class="infobulle"
            style={{ left: `${info().gauche}%`, top: `${info().haut}%` }}
            aria-hidden="true"
          >
            <strong>{info().projet.acronyme}</strong>
            <Show when={info().projet.planAction}>
              <div>{info().projet.planAction}</div>
            </Show>
            <Show when={info().proche}>
              {(p) => (
                <div>
                  le plus proche : {p().acronyme} · {formaterScore(p().score)}
                </div>
              )}
            </Show>
            <div>{pluriel(info().liens, "lien")} à ce seuil</div>
          </div>
        )}
      </Show>
    </div>
  );
}
